import { platform } from '@pay24/common'
import AsyncStorage from '@react-native-async-storage/async-storage'
import axios, { type AxiosResponse } from 'axios'
import type { SnapshotIn } from 'mobx-state-tree'
import { stringify } from 'query-string'
import { Platform } from 'react-native'
import { Company } from './store/erp/models'

declare module 'axios' {
  export interface AxiosRequestConfig {
    signal?: AbortSignal
  }
}

const WALLET_URL = platform.wallet.url

const STORE24_URL = platform.store24.url
const KEEP_URL = platform.erp.url

const paramsSerializer = (params: any) => {
  if (!params) return stringify({ ...params })
  if (!params.filter || typeof params.filter !== 'object')
    return stringify({ ...params })
  const { filter, ...rest } = params
  const filter_params = Object.entries(filter).reduce(
    (_filter, [key, value]) => {
      if (typeof value === 'string') {
        if (value.trim().length > 0) _filter[`filter[${key}]`] = value
      } else if (typeof value === 'number') {
        _filter[`filter[${key}]`] = value
      } else if (typeof value === 'boolean') {
        _filter[`filter[${key}]`] = value
      } else if (Array.isArray(value)) {
        if (value.length > 0) _filter[`filter[${key}]`] = value.join(',')
      }
      return _filter
    },
    {},
  )
  return stringify({ ...rest, ...filter_params })
}

export const walletApi = axios.create({ baseURL: WALLET_URL })
export const store24Api = axios.create({ baseURL: STORE24_URL })
export const keepApi = axios.create({ baseURL: KEEP_URL, paramsSerializer })

walletApi.interceptors.request.use(async (value) => {
  if (value.signal && !value.cancelToken) {
    const cancelTokenSource = axios.CancelToken.source()
    value.signal.addEventListener('abort', () => cancelTokenSource.cancel())
    value.cancelToken = cancelTokenSource.token
  }
  return value
})

walletApi.interceptors.response.use((value) => {
  if (typeof value.data.result === 'number' && value.data.result !== 0)
    throw new Error(value.data.message || value.statusText)
  return value
})

const accessToken = async () => {
  try {
    const login_response = await AsyncStorage.getItem('login-response')
    if (login_response) {
      const { token } = JSON.parse(login_response) as LoginResponse
      return token
    }
  } catch {}
  return null
}

const STORE24_ERROR_CODES = {
  USER_NOT_AUTHORIZED: 1,
  USER_BLOCKED: 5,
} as const

class Store24ApiError extends Error {
  readonly response: AxiosResponse
  constructor(response: AxiosResponse) {
    const message = response.data.message || response.statusText
    super(message)
    this.response = response
  }

  get is_login_error() {
    return (
      this.response.data.result === STORE24_ERROR_CODES.USER_NOT_AUTHORIZED ||
      this.response.data.result === STORE24_ERROR_CODES.USER_BLOCKED
    )
  }
}

store24Api.interceptors.request.use(async (value) => {
  if (value.signal && !value.cancelToken) {
    const cancelTokenSource = axios.CancelToken.source()
    value.signal.addEventListener('abort', () => cancelTokenSource.cancel())
    value.cancelToken = cancelTokenSource.token
  }
  return value
})

store24Api.interceptors.request.use(async (value) => {
  const token = await accessToken()
  if (token) value.headers.Authorization = `Bearer ${token}`
  return value
})

store24Api.interceptors.response.use((value) => {
  if (typeof value.data.result === 'number' && value.data.result !== 0)
    throw new Store24ApiError(value)
  return value
})

export const isStore24ApiError = (error: any): error is Store24ApiError =>
  error instanceof Store24ApiError

const KEEP_ERROR_CODES = {
  USER_NOT_AUTHORIZED: 1,
  USER_BLOCKED: 5,
} as const

class KeepApiError extends Error {
  readonly response: AxiosResponse
  constructor(response: AxiosResponse) {
    const message = response.data.message || response.statusText
    super(message)
    this.response = response
  }

  get is_login_error() {
    return (
      this.response.data.result === KEEP_ERROR_CODES.USER_NOT_AUTHORIZED ||
      this.response.data.result === KEEP_ERROR_CODES.USER_BLOCKED
    )
  }
}

keepApi.interceptors.request.use(async (value) => {
  if (value.signal && !value.cancelToken) {
    const cancelTokenSource = axios.CancelToken.source()
    value.signal.addEventListener('abort', () => cancelTokenSource.cancel())
    value.cancelToken = cancelTokenSource.token
  }
  return value
})

keepApi.interceptors.request.use(async (value) => {
  const token = await accessToken()
  if (token) value.headers.Authorization = `Bearer ${token}`
  return value
})

const decodeUtf8 = (bytes: Uint8Array) => {
  if (typeof TextDecoder !== 'undefined')
    return new TextDecoder('utf-8').decode(bytes)
  let encoded = ''
  bytes.forEach((byte) => {
    encoded += '%' + byte.toString(16).padStart(2, '0')
  })
  return decodeURIComponent(encoded)
}

const decodeBinaryError = async (value: AxiosResponse) => {
  const contentType = String(value.headers?.['content-type'] || '')
  if (!contentType.includes('application/json')) return null
  try {
    let text: string | null = null
    if (value.data instanceof ArrayBuffer)
      text = decodeUtf8(new Uint8Array(value.data))
    else if (typeof Blob !== 'undefined' && value.data instanceof Blob)
      text = await value.data.text()
    else if (typeof value.data === 'string') text = value.data
    if (!text) return null
    const data = JSON.parse(text)
    return typeof data?.result === 'number' && data.result !== 0 ? data : null
  } catch {
    return null
  }
}

keepApi.interceptors.response.use(async (value) => {
  if (typeof value.data.result === 'number' && value.data.result !== 0)
    throw new KeepApiError(value)
  if (
    value.config?.responseType === 'arraybuffer' ||
    value.config?.responseType === 'blob'
  ) {
    const error = await decodeBinaryError(value)
    if (error) throw new KeepApiError({ ...value, data: error })
  }
  return value
})

export const isKeepApiError = (error: any): error is KeepApiError =>
  error instanceof KeepApiError

type LoginResponse = {
  token: string
  user: { phone_number: string; id: number }
  result: 0
}

export const login = async (payload: {
  username: string
  password: string
}) => {
  const { data } = await keepApi.post('/api/v1/user/login', {
    ...payload,
    info: { device: Platform.OS },
  })
  await AsyncStorage.setItem('login-response', JSON.stringify(data))
  return data as LoginResponse
}

export const forgotPassword = async (phone: string) => {
  await keepApi.post('/api/v1/user/forgot_password', { phone: phone.trim() })
}

// компания у деплоя одна и определяется его конфигурацией — store24 больше не участвует
export const fetchCompanies = async () => {
  const { data } = await keepApi.get('/api/v1/keep24/company/current')
  return [data.item] as SnapshotIn<typeof Company>[]
}

export const fetchCompany = async (_id: number, signal: AbortSignal) => {
  const { data } = await keepApi.get('/api/v1/keep24/company/current', {
    signal,
  })
  return data.item as SnapshotIn<typeof Company>
}

export const saveCompany = async (
  company: Omit<SnapshotIn<typeof Company>, 'user'>,
) => {
  return company
}

export const createCompany = async (
  company: Omit<SnapshotIn<typeof Company>, 'id' | 'user'> & {
    id?: number | null
  },
  token: string,
) => {
  const { data } = await store24Api.post('/api/v1/keep/companies', company, {
    params: { token, platform: Platform.OS },
  })
  return data.item as SnapshotIn<typeof Company>
}

export const getCompany = async (signal: AbortSignal) => {
  const { token, cancel } = axios.CancelToken.source()
  signal.addEventListener('abort', () => cancel())
  const { data } = await keepApi.get('/api/v1/keep24/settings/company', {
    cancelToken: token,
  })
  return data.item
}

export const sign = async (doc) => {
  const { data } = await keepApi.post(
    '/api/v1/keep24/cds/get-sign/for-hash',
    doc,
  )
  return data.response
}

export const cdsGetPinCode = async () => {
  const { data } = await keepApi.post('/api/v1/keep24/cds/get-pin-code')
  return data.response
}

export const cdsAuthMethods = async () => {
  const { data } = await keepApi.get('/api/v1/keep24/cds/user-auth-methods')
  return data.response
}

export const cdsChangeAuthMethod = async (method) => {
  const { data } = await keepApi.post(
    '/api/v1/keep24/cds/change-user-auth-methods',
    method,
  )
  return data.response
}

export const cdsSendCode = async (code) => {
  const { data } = await keepApi.post('/api/v1/keep24/cds/auth', code)
  return data.response
}

export const saveNotificationToken = async (body) => {
  // пуши регистрировались в store24; для insure24 push-канала пока нет
}
